import { AlarmClock, Clock, PhoneCall } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader } from "@/components/ui/card";
import { callDue } from "@/lib/cadence";
import { seesCampaigns, type LeadCadence, type LeadCard } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";
import { useAuth } from "@/lib/auth";

/**
 * The Next call card beside a contact record: when the lead's next call is due on each campaign
 * that calls them, and nothing more (ACL-575). It was the Follow-up card, the lead's journey on
 * each cadence the way the dialer reads it (ADR-066); nobody is shown the cadence, its phases,
 * attempts or priority now. Those stay on the campaign's pages, and the pause, resume and
 * re-enroll actions in the record's header menu. Only an admin and up is shown it (ACL-642).
 */

/** The lead's next call on each campaign that has one coming. Nothing when none has. */
export function NextCallCard({
  contact, cadence, now,
}: {
  contact: LeadCard;
  cadence: LeadCadence | undefined;
  now: number;
}) {
  const rows = (cadence?.memberships ?? [])
    .filter((m) => m.cadence)
    .flatMap((m) => {
      const due = callDue(m, now, contact.text_first_until ?? null);
      return due ? [{ m, due }] : [];
    });
  const sees = seesCampaigns(useAuth().user?.role);
  if (rows.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <CardHeader title="Next call" />
      <ul className="divide-y divide-border">
        {rows.map(({ m, due }) => {
          const Icon = due.kind !== "due" ? PhoneCall : due.overdue ? AlarmClock : Clock;
          return (
            <li key={m.campaign_lead_id} className="flex items-start gap-3 px-4 py-3">
              <span
                className={cn(
                  "mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full ring-1",
                  due.overdue ? "bg-warning-2 text-warning-11 ring-warning-4" : "bg-accent-2 text-accent-11 ring-accent-4",
                )}
                aria-hidden
              >
                <Icon className="size-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold text-text">
                  {due.text}
                  {due.overdue && (
                    <Badge tone="warning" size="sm" title="Due for more than fifteen minutes with nobody taking it">
                      Overdue
                    </Badge>
                  )}
                </p>
                <p className="truncate text-xs text-text-muted">
                  {[due.at ? formatDateTime(due.at) : null, sees ? (m.campaign_name ?? "Deleted campaign") : null].filter(Boolean).join(" · ")}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
